import { computePlateLayout, estimatedTimeMinutes, physicalSize } from '../pipeline/estimate';

/**
 * Tailles de kit (Lot P3 — CDC §4.4 étape 2).
 *
 * Chaque preset fixe la PLUS GRANDE dimension de la grille (en perles) ;
 * l'autre dimension suit le ratio du cadrage client (plaques partielles
 * autorisées, on facture les plaques entamées). Le « pitch » vend la
 * netteté, jamais les pixels.
 */
export type SizeKey = 'S' | 'M' | 'L' | 'XL' | 'XXL';
/** Sélection de taille du tunnel : presets ou « Personnalisée ». */
export type AnySizeKey = SizeKey | 'custom';

export type SizePreset = {
  key: SizeKey;
  /** Libellé produit (jamais technique). */
  label: string;
  /** Argument de vente en un souffle. */
  tagline: string;
  /** Plus grande dimension de la grille, en perles (multiple de 29). */
  maxSide: number;
};

export const SIZE_PRESETS: ReadonlyArray<SizePreset> = [
  { key: 'S', label: 'Rétro', tagline: "L'effet pixel assumé", maxSide: 29 },
  { key: 'M', label: 'Essentiel', tagline: 'Le bon équilibre', maxSide: 58 },
  { key: 'L', label: 'Portrait net', tagline: 'Les visages prennent vie', maxSide: 87 },
  { key: 'XL', label: 'Grand format', tagline: 'Effet tableau, détails fins', maxSide: 116 },
  { key: 'XXL', label: 'Fresque', tagline: 'La pièce murale qui en impose', maxSide: 145 },
];

export function sizePreset(key: SizeKey): SizePreset {
  const p = SIZE_PRESETS.find((s) => s.key === key);
  if (!p) throw new Error(`Taille inconnue : ${key}`);
  return p;
}

/** Dimension minimale d'un côté de grille (en dessous, le rendu n'a plus de sens). */
const MIN_SIDE = 8;

/** Bornes de la taille « Personnalisée » (plus grande dimension, en perles).
 *  500 = 2,5 m — les fresques murales sont permises : on livre des kits,
 *  l'ambition (et le budget) sont ceux du client. 12 = 6 cm, en dessous
 *  rien n'est lisible. */
export const CUSTOM_MIN_SIDE = 12;
export const CUSTOM_MAX_SIDE = 500;

/** Preset synthétique pour la taille « Personnalisée » choisie par le client
 *  (plus grande dimension en perles, clampée aux bornes ci-dessus). */
export function customPreset(maxSide: number): SizePreset {
  const side = Math.max(
    CUSTOM_MIN_SIDE,
    Math.min(CUSTOM_MAX_SIDE, Math.round(Number.isFinite(maxSide) ? maxSide : CUSTOM_MIN_SIDE)),
  );
  return {
    key: 'XL', // clé structurelle inutilisée pour le custom (cf. AnySizeKey)
    label: 'Personnalisée',
    tagline: 'Exactement la taille que tu veux',
    maxSide: side,
  };
}

export type GridDims = { width: number; height: number };

/**
 * Grille cible pour un preset de taille et un ratio de cadrage (largeur/hauteur).
 * La plus grande dimension = `maxSide`, l'autre suit le ratio (≥ MIN_SIDE).
 */
export function gridForAspect(preset: SizePreset, aspect: number): GridDims {
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  if (a >= 1) {
    return {
      width: preset.maxSide,
      height: Math.max(MIN_SIDE, Math.round(preset.maxSide / a)),
    };
  }
  return {
    width: Math.max(MIN_SIDE, Math.round(preset.maxSide * a)),
    height: preset.maxSide,
  };
}

export type SizeFacts = {
  dims: GridDims;
  beads: number;
  plates: number;
  widthCm: number;
  heightCm: number;
  timeMinutes: number;
};

/** Faits physiques d'une taille pour un ratio donné (cartes de l'étape 2). */
export function sizeFacts(preset: SizePreset, aspect: number): SizeFacts {
  const dims = gridForAspect(preset, aspect);
  const layout = computePlateLayout(dims.width, dims.height, 29, 29);
  const size = physicalSize(dims.width, dims.height);
  const beads = dims.width * dims.height;
  return {
    dims,
    beads,
    plates: layout.cols * layout.rows,
    widthCm: size.widthCm,
    heightCm: size.heightCm,
    timeMinutes: estimatedTimeMinutes(beads),
  };
}

/** Formatage humain du temps de pose (« ~2 h 10 », « ~45 min »). */
export function formatTime(minutes: number): string {
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `~${h} h` : `~${h} h ${String(m).padStart(2, '0')}`;
}
